import { cn } from "@/lib/utils";
import { type ComponentProps, useEffect, useState } from "react";

/**
 * Place inside a relatively positioned element to display an overlay that has a progress bar at the top
 * This will make the element appear as if its contents are loading while it's still rendered
 */
export default function LoadingOverlay({
  loading,
  className,
  ...props
}: ComponentProps<"div"> & {
  loading?: boolean;
}) {
  const [hasBeenVisible, setHasBeenVisible] = useState(loading);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (loading) {
      // Show immediately when loading starts
      timeout = setTimeout(() => setHasBeenVisible(true), 0);
    } else if (hasBeenVisible) {
      // Delay hiding to allow fade-out animation
      timeout = setTimeout(() => setHasBeenVisible(false), 300);
    }

    return () => clearTimeout(timeout);
  }, [loading, hasBeenVisible]);

  if (!hasBeenVisible) return null;

  return (
    <div
      role="status"
      className={cn(
        "absolute inset-0 z-10 backdrop-blur-[2px] transition-opacity duration-300",
        loading ? "opacity-100" : "opacity-0",
        className,
      )}
      aria-label="Loading"
      {...props}
    >
      <div className="relative z-10 h-0.5 w-full overflow-hidden bg-transparent">
        <div className="bg-foreground-pressed animate-progress-bar absolute inset-0 w-full" />
      </div>
    </div>
  );
}
